<template>
  <div class="room-selector-container">
    <div class="room-selector-header">
      <div class="room-selector-header-title">
        <InfoPopup
          v-if="infoPopupRoomReference"
          v-bind="infoPopupRoomReference"
          @open="open = true"
          @close="open = false"
          :open="open"
        />
        <h1 class="heading">Referentie per ruimte</h1>
      </div>
      <button
        v-if="currentStep === 2 && rooms.length > 1"
        class="btn-select-all-rooms"
        :class="{ selected: allRoomsSelected }"
        @click="selectAllRooms"
      >
        <div class="select-area">
          <div class="dot">
            <div class="inner"></div>
          </div>
        </div>
        <span>Alle ruimtes selecteren</span>
      </button>
    </div>
    <div class="room-selector-content">
      <div
        @click.stop="setActiveRoom(room.id)"
        class="room"
        :id="`room-${room.id}`"
        :class="{
          active: activeRoom === room.id || allRoomsSelected,
          line: room.line,
        }"
        v-for="(room, roomIndex) in rooms"
        :key="room.id"
      >
        <RoomPopup
          :open="openRoomPopup === room.id"
          :room="room"
          @close="openRoomPopup = null"
        />
        <div class="room-details">
          <span>
            {{ room.name || room.roomType || `Ruimte ${roomIndex + 1}` }}</span
          >
          <div v-if="room.line" class="room-line">
            <img
              v-if="room.line.brand_logo"
              :src="room.line.brand_logo"
              alt="brand_logo"
            />
            <span v-if="room.line.color_name">{{ room.line.color_name }}</span>
            <span v-else>{{ getColor(room.line.color_id) }}</span>
          </div>
        </div>
        <div
          class="room-icons"
          :class="{
            column: shouldShowRoomWarningIcon[roomIndex] && someRoomsHaveLine,
            row:
              (!shouldShowRoomWarningIcon[roomIndex] && !someRoomsHaveLine) ||
              !someRoomsHaveLine,
          }"
        >
          <img
            v-if="shouldShowRoomWarningIcon[roomIndex]"
            :src="getIcon('alert-circle-warning.svg')"
            class="warning-icon"
            v-tooltip="'Selecteer producten voor deze kamer'"
          />
          <button
            v-if="currentStep < 2"
            @click.prevent="openRoomPopup = room.id"
            class="icon-button"
          >
            <img :src="getIcon('pencil.svg')" />
          </button>
        </div>
      </div>
      <div v-if="currentStep === 1" @click="addRoom" class="new-room">+</div>
    </div>
    <transition name="fade" mode="out-in">
      <div class="backdrop" v-if="loading"></div>
    </transition>
  </div>
</template>
<script>
import InfoPopup from '@/components/popups/InfoPopup.vue';
import { mapGetters, mapState } from 'vuex';
import RoomPopup from '@/components/popups/RoomPopup.vue';

export default {
  name: 'RoomSelector',
  components: { RoomPopup, InfoPopup },
  data() {
    return {
      open: false,
      openRoomPopup: null,
      loading: false,
      visitedRoomIds: [],
    };
  },
  computed: {
    ...mapState('build', ['activeRoom']),
    ...mapState('roomOptions', ['roomTypes', 'wallTypes']),
    ...mapState('step', ['currentStep']),
    ...mapState('brand-color-selection', ['colors', 'allRoomsSelected']),
    ...mapState('data', ['infoPopups']),
    ...mapGetters({
      rooms: 'build/getRooms',
    }),
    infoPopupRoomReference() {
      return this.$store.getters['data/findInfoPopupByKey'](
        'switch_room_reference'
      );
    },
    shouldShowRoomWarningIcon() {
      // Array of booleans that determine if the warning icon should be shown for each room.
      // Within the v-for we can use its index to get the corresponding value.
      return this.rooms.map((room) => {
        if (this.currentStep === 1) {
          return room.products.length === 0;
        }

        if (this.currentStep === 2) {
          return !room.line;
        }
      });
    },
    someRoomsHaveLine() {
      return this.rooms.some((room) => room.line);
    },
  },
  methods: {
    async addRoom() {
      this.$store.commit('build/addRoom', {
        name: null,
        roomType: null,
        wallType: null,
        squareMeters: 0,
        ceilingHeightInCm: 0,
        products: [],
        line: null,
        isSaved: false,
      });

      await this.$store.dispatch('build/makeRoom', this.activeRoom);

      this.openRoomPopup = this.activeRoom;
    },
    async setActiveRoom(roomId) {
      // Room could be deleted at this point.
      const roomExists = this.rooms.some((r) => r.id === roomId);
      if (!roomExists) {
        return;
      }

      if (this.activeRoom === roomId || this.allRoomsSelected) {
        return;
      }

      this.visitedRoomIds.push(roomId);

      if (this.currentStep === 1) {
        this.$store.commit('build/setActiveRoom', roomId);

        return;
      }

      this.loading = true;
      this.$store.commit('build/setActiveRoom', roomId);
      await this.$store.dispatch('brand-color-selection/getBrandColorTypes');
      this.loading = false;
    },
    async selectAllRooms() {
      this.loading = true;

      this.$store.commit(
        'brand-color-selection/setAllRoomsSelected',
        !this.allRoomsSelected
      );
      await this.$store.dispatch('brand-color-selection/getBrandColorTypes');

      this.loading = false;
    },
    getColor(colorId) {
      return this.colors.find((color) => color.id === colorId)?.name || '';
    },
  },
  beforeDestroy() {
    this.visitedRoomIds = [];
  },
};
</script>
